import { useRef } from 'react';
import { useAchievementTypes, useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { addMonths } from 'date-fns';
import { ModalButtonWithForm, User } from 'components/common';
import {
  Datepicker,
  FormGroup,
  MultipleAttachments,
  TextArea
} from 'components/common/form';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';

import type { FormApi } from 'informed';
import type { AchievementData, UserWithBasicData } from 'shared-types';

interface Props {
  isDropdown?: boolean;
  isUserWithoutCompletion?: boolean;
  updateUsers: () => Promise<void>;
  user: UserWithBasicData & { achievementData: AchievementData };
}

const AchievementCompletionsUpdate = ({
  isDropdown = false,
  isUserWithoutCompletion,
  updateUsers,
  user
}: Props) => {
  const { dispatch } = useDispatch();

  const formApi = useRef<FormApi<typeof initialValues>>(undefined);
  const uploader = useRef<any>(undefined);

  const {
    language,
    messages,
    pageState: { achievement }
  } = useReduxState();

  const { hasCompletion, hasExpiration, hasStartedAt } = useAchievementTypes({
    type: achievement?.type
  });

  const handleDateChange = (
    value: number,
    field: 'completion' | 'expiration'
  ) => {
    if (!formApi.current) return;

    const { completionDate, expirationDate } =
      formApi.current.getState().values;

    if (achievement.monthsBeforeExpiration && field === 'completion') {
      formApi.current.setValue(
        'expirationDate',
        completionDate
          ? addMonths(completionDate, achievement.monthsBeforeExpiration)
          : null
      );
    }

    if (completionDate && expirationDate) {
      if (field === 'expiration' && value <= completionDate) {
        formApi.current.setValue('completionDate', null);
      }

      if (field === 'completion' && value >= expirationDate) {
        formApi.current.setValue('expirationDate', null);
      }
    }
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      if (user.achievementData.comments && !values.comments) {
        values.comments = null;
      }
      if (user.achievementData.canSeeAttachments) {
        values.attachments = await uploader.current();
      }

      const userAttributes = {};

      Object.keys(values).forEach((fieldKey) => {
        if (fieldKey.startsWith('userAttributes-')) {
          const attributeId = fieldKey.split('-')[1];
          userAttributes[attributeId] = values[fieldKey];
        }
      });

      await api.put(`/admin/achievements/${achievement.id}/completions`, {
        createCompletionIfMissing: isUserWithoutCompletion,
        updatedCompletion: { ...values, userAttributes },
        userId: user.id
      });

      await updateUsers();

      dispatch.addNotice({
        text: messages.usersEditUserSuccess,
        type: 'success'
      });
    } catch (err) {
      if (err.message === messages.commonUploadFileFailed) {
        return false; // keep the modal open, user already notified by the uploader
      } else {
        await dispatch.handleErrorDispatch(err);
      }
    }
  };

  const setFormApi = (api: FormApi<typeof initialValues>) =>
    (formApi.current = api);

  const initialValues = {
    attachments: user.achievementData.attachments,
    comments: user.achievementData.comments,
    completionDate: Date.parse(user.achievementData.completionDate as string),
    expirationDate: Date.parse(user.achievementData.expirationDate as string),
    startedAtDate: user.achievementData.startedAtDate
      ? Date.parse(user.achievementData.startedAtDate as string)
      : undefined,
    ...Object.keys(user.achievementData.userAttributes || {}).reduce(
      (fields, attrId) => {
        fields[`userAttributes-${attrId}`] =
          user.achievementData.userAttributes?.[attrId];
        return fields;
      },
      {}
    )
  };

  return (
    <ModalButtonWithForm
      buttonCategory="lighter"
      buttonIcon="pen"
      buttonIsDropdown={isDropdown}
      buttonSize="small"
      buttonText={isDropdown ? messages.commonUpdateDetails : undefined}
      buttonProps={
        isDropdown
          ? undefined
          : {
              'aria-label': messages.commonUpdateDetails,
              'data-tooltip': messages.commonUpdateDetails
            }
      }
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={messages.commonSave}
      modalAutoFocus={false}
      modalTitle={messages.achievementCompletionDetails}
      modalWidth={'medium'}
      onSubmit={handleSubmit}
      setFormApi={setFormApi}
    >
      {({ values }) => (
        <div className="content">
          <User
            enableProfileLink={true}
            user={user}
            style={{ marginBottom: 10 }}
          />

          {hasStartedAt || hasCompletion || hasExpiration ? (
            <div className="date-fields">
              {hasStartedAt ? (
                <FormGroup
                  label={messages.commonStartDate}
                  style={{ marginTop: 0 }}
                >
                  <Datepicker
                    field="startedAtDate"
                    id="startedAtDate"
                    isClearable={true}
                    maxDate={values?.completionDate}
                    placeholder={messages.excelDate}
                    simpleClear={true}
                    style={{ width: 170 }}
                  />
                </FormGroup>
              ) : null}

              {hasCompletion ? (
                <FormGroup
                  label={messages.completionsCompletionDate}
                  style={{ marginTop: 0 }}
                >
                  <Datepicker
                    field="completionDate"
                    id="completionDate"
                    isClearable={true}
                    placeholder={messages.publishStartDateEmpty}
                    onChange={(value: number) =>
                      handleDateChange(value, 'completion')
                    }
                    simpleClear={true}
                    style={{ width: 170 }}
                  />
                </FormGroup>
              ) : null}

              {hasExpiration ? (
                <FormGroup
                  label={messages.commonExpirationDate}
                  style={{ marginTop: 0 }}
                >
                  <Datepicker
                    disabled={achievement.monthsBeforeExpiration}
                    field="expirationDate"
                    id="expirationDate"
                    isClearable={true}
                    minDate={values?.completionDate}
                    placeholder={messages.publishEndDateEmpty}
                    onChange={(value: number) =>
                      handleDateChange(value, 'expiration')
                    }
                    simpleClear={true}
                    style={{ width: 170 }}
                  />
                </FormGroup>
              ) : null}
            </div>
          ) : null}

          <FormGroup label={messages.commonComment} maxWidth="100%">
            <TextArea
              autoGrow={true}
              field="comments"
              id={`achievement-comments-${user.id}`}
              rows={4}
            />
          </FormGroup>

          {user.achievementData.canSeeAttachments ? (
            <MultipleAttachments
              getUploadHandler={(fn) => (uploader.current = fn)}
            />
          ) : null}

          <div className="user-attributes">
            {achievement.userAttributes?.map((attribute) => (
              <FormGroup
                key={attribute.id}
                label={
                  attribute.titleTranslations?.[language] || attribute.title
                }
                labelIsOptional={!attribute.isRequired}
              >
                <ProductUserCustomAttributeRenderFormField
                  attribute={attribute}
                />
              </FormGroup>
            ))}
          </div>

          <style jsx>{`
            .date-fields {
              display: flex;
              flex-direction: row;
              flex-wrap: wrap;
              align-items: flex-end;
              justify-content: space-between;
              gap: 10px;
              margin-top: 15px;
              width: 100%;
            }
            .user-attributes {
              margin-top: 30px;
            }
          `}</style>
        </div>
      )}
    </ModalButtonWithForm>
  );
};

export default AchievementCompletionsUpdate;
